import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  getTeacherAccountLookup,
  requestPasswordReset,
  resendTeacherSetupLink
} from "../services/TeacherAPIClient";
import { isPlaceholderWorkflowId } from "../utils/teacherWorkflowRequests";
import "./TeacherResolveLoginIssueModal.css";

const LOGIN_ISSUES = [
  { value: "forgot-password", label: "They forgot their password" },
  { value: "not-activated", label: "They have not finished setting up their account" },
  { value: "no-invitation", label: "They did not receive an invitation" },
  { value: "forgot-email", label: "They do not know which email to use" },
  { value: "other", label: "Something else" }
];

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  email: String(prefill?.email || "").trim(),
  loginIssue: String(prefill?.loginIssue || prefill?.login_issue || "").trim()
});

const normaliseTeacher = (teacher, account, canReset) => {
  const availableActions = teacher?.availableActions || teacher?.available_actions || [];
  const id =
    teacher?.pendingteacherid ||
    teacher?.pendingTeacherId ||
    teacher?.id ||
    teacher?._id ||
    teacher?.teacherid ||
    teacher?.teacherId ||
    teacher?.teacher_id;
  const name =
    teacher?.name ||
    teacher?.fullName ||
    teacher?.displayName ||
    [teacher?.firstName, teacher?.lastName].filter(Boolean).join(" ") ||
    "Unnamed teacher";
  const email = teacher?.email || "";
  const status = teacher?.status || teacher?.accountStatus || teacher?.account_status || account;
  const canSendReset = availableActions.includes("send_password_reset") || canReset;

  const normalisedStatus = normaliseText(status).replace(/[_-]+/g, " ");
  const statusIndicatesSetupIncomplete = ["pending", "invite", "setup incomplete", "not activated", "awaiting setup"]
    .some((value) => normalisedStatus.includes(value));
  const statusIndicatesActive = ["active", "enabled", "setup complete"]
    .some((value) => normalisedStatus.includes(value));
  const statusType = normalisedStatus.includes("no account") || normalisedStatus.includes("not found")
    ? "no-account"
    : statusIndicatesSetupIncomplete
      ? "setup-incomplete"
      : statusIndicatesActive || canSendReset
        ? "active"
        : "setup-incomplete";
  const readableStatus = statusType === "active"
    ? "Active account"
    : statusType === "no-account"
      ? "No account"
      : "Setup incomplete";

  return {
    ...teacher,
    id,
    pendingteacherid: teacher?.pendingteacherid || teacher?.pendingTeacherId || teacher?.pending_teacher_id || id,
    rowKey: id || email || name,
    name,
    email,
    account: status,
    statusType,
    readableStatus,
    availableActions,
    detail: canSendReset
      ? "Active account"
      : "Setup incomplete",
    primaryAction: canSendReset ? "Send password reset link" : "Send setup link",
    sendAction: canSendReset ? "Send reset link" : "Send setup link",
    footerCopy: canSendReset ? "Password reset recommended." : "Setup link recommended."
  };
};

const mergeTeacherRows = (teachers) => {
  const rowsByKey = new Map();

  teachers.forEach((teacher) => {
    const key = normaliseText(teacher.email || teacher.name || teacher.id);
    if (key && !rowsByKey.has(key)) {
      rowsByKey.set(key, teacher);
    }
  });

  return Array.from(rowsByKey.values());
};

const findPrefilledTeacher = (teachers, prefillContext) => {
  const teacherName = normaliseText(prefillContext.teacherName);
  const email = normaliseText(prefillContext.email);

  if (!teacherName && !email) {
    return null;
  }

  return (
    teachers.find((teacher) => email && normaliseText(teacher.email) === email) ||
    teachers.find((teacher) => teacherName && normaliseText(teacher.name) === teacherName) ||
    null
  );
};

const getLookupRows = (lookupResponse) => {
  if (Array.isArray(lookupResponse)) return lookupResponse;
  if (Array.isArray(lookupResponse?.teachers)) return lookupResponse.teachers;
  if (Array.isArray(lookupResponse?.rows)) return lookupResponse.rows;
  if (Array.isArray(lookupResponse?.accounts)) return lookupResponse.accounts;
  return [];
};

function TeacherResolveLoginIssueModal({
  show,
  handleClose,
  schoolid,
  prefill = null,
  onUpdateUserEmail = null,
  onInviteTeacher = null,
  onContactSupport = null,
  onSendPasswordReset = null,
  onSendAccountSetup = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [teacherSearch, setTeacherSearch] = useState("");
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [actionTeacher, setActionTeacher] = useState(null);
  const [sentTeacher, setSentTeacher] = useState(null);
  const [sentAction, setSentAction] = useState("Send reset link");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginIssue, setLoginIssue] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setTeacherSearch(prefillContext.teacherName || "");
    setTeachers([]);
    setSelectedTeacher(null);
    setActionTeacher(null);
    setSentTeacher(null);
    setSentAction("Send reset link");
    setError("");
    setIsLoading(false);
    setIsSubmitting(false);
    setLoginIssue(prefillContext.loginIssue);

    if (!schoolid) {
      setError("Teachers could not be loaded. Please try again.");
      return;
    }

    setIsLoading(true);
    getTeacherAccountLookup(schoolid)
      .then((lookupResponse) => {
        const apiRows = getLookupRows(lookupResponse).map((teacher) =>
          normaliseTeacher(
            teacher,
            teacher?.accountStatus || teacher?.account_status || "Unknown",
            (teacher?.availableActions || teacher?.available_actions || []).includes("send_password_reset")
          )
        );
        const mergedRows = mergeTeacherRows(apiRows).sort((firstTeacher, secondTeacher) =>
          firstTeacher.name.localeCompare(secondTeacher.name)
        );

        setTeachers(mergedRows);
        setSelectedTeacher(findPrefilledTeacher(mergedRows, prefillContext));
      })
      .catch((loadError) => {
        console.error("Error loading login issue teachers:", loadError);
        setError("Teachers could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, schoolid, prefillContext]);

  const visibleTeachers = useMemo(() => {
    const query = normaliseText(teacherSearch);

    if (!query) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.account, teacher.detail].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [teacherSearch, teachers]);

  const selectedSummary = selectedTeacher;
  const recommendation = useMemo(() => {
    if (!selectedTeacher || !loginIssue) return null;
    if (selectedTeacher.statusType === "no-account") return { type: "invite", label: "Invite teacher" };
    if (loginIssue === "forgot-email") return { type: "email", label: "Update email" };
    if (selectedTeacher.statusType === "setup-incomplete") return { type: "setup", label: "Resend setup link" };
    if (loginIssue === "forgot-password" || loginIssue === "no-invitation") return { type: "reset", label: "Send password reset link" };
    return { type: "support", label: "Continue to support" };
  }, [selectedTeacher, loginIssue]);

  const sendSelectedAction = async (action, teacher) => {
    if (!teacher || isSubmitting) {
      return;
    }

    setError("");

    if (action === "Send setup link" && !teacher.id) {
      setError("Choose a listed invited teacher before sending a setup link.");
      return;
    }

    if (action === "Send reset link" && !teacher.email) {
      setError("This teacher does not have an email address for password reset.");
      return;
    }

    setIsSubmitting(true);

    try {
      if (action === "Send setup link") {
        const pendingTeacherId = teacher.pendingteacherid || teacher.id;

        if (isPlaceholderWorkflowId(pendingTeacherId)) {
          throw new Error("Missing real pending teacher id.");
        }

        await resendTeacherSetupLink(schoolid, pendingTeacherId);
      } else if (teacher.email) {
        await requestPasswordReset(teacher.email);
      }

      setSentTeacher(teacher);
      setSentAction(action);
      setStage("success");
    } catch (submitError) {
      console.error("Error resolving login issue:", submitError);
      setError("Login help could not be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrimaryAction = () => {
    if (!loginIssue || !selectedTeacher) {
      return;
    }
    setActionTeacher(selectedTeacher);
    setStage("actions");
  };

  const runRecommendedAction = () => {
    const context = { teacherName: selectedTeacher?.name, email: selectedTeacher?.email };
    if (recommendation?.type === "reset") {
      return onSendPasswordReset
        ? onSendPasswordReset({ ...context, loginIssue })
        : sendSelectedAction("Send reset link", selectedTeacher);
    }
    if (recommendation?.type === "setup") {
      return onSendAccountSetup
        ? onSendAccountSetup({ ...context, loginIssue })
        : sendSelectedAction("Send setup link", selectedTeacher);
    }
    if (recommendation?.type === "invite") return onInviteTeacher?.(context);
    if (recommendation?.type === "email") return onUpdateUserEmail?.({ teacherName: selectedTeacher?.name, currentEmail: selectedTeacher?.email });
    return onContactSupport?.({
      ...context,
      issueType: "Login or QR code",
      teacherId: selectedTeacher?.id,
      teacherName: selectedTeacher?.name,
      description: `Login issue: ${LOGIN_ISSUES.find((issue) => issue.value === loginIssue)?.label || loginIssue}`
    });
  };

  const handleTeacherSearchChange = (event) => {
    const nextSearch = event.target.value;
    setTeacherSearch(nextSearch);
    setError("");

    if (
      selectedTeacher &&
      normaliseText(nextSearch) !== normaliseText(selectedTeacher.name)
    ) {
      setSelectedTeacher(null);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherSearch(teacher.name);
    setError("");
  };

  const resetForAnotherIssue = () => {
    setStage("form");
    setTeacherSearch("");
    setSelectedTeacher(null);
    setActionTeacher(null);
    setSentTeacher(null);
    setSentAction("Send reset link");
    setLoginIssue("");
    setError("");
  };

  const handleDone = () => {
    const teacher = sentTeacher || selectedSummary;

    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: teacher?.email || teacher?.name || "",
        message: "Login issue workflow complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherRow = (teacher) => {
    const selected = selectedTeacher?.rowKey === teacher.rowKey;

    return (
      <button
        key={teacher.rowKey}
        type="button"
        className={`resolve-login-row${selected ? " selected" : ""}`}
        onClick={() => handleTeacherSelect(teacher)}
        aria-label={`Select ${teacher.name}`}
        aria-pressed={selected}
      >
        <span className="resolve-login-radio" aria-hidden="true" />
        <span className="resolve-login-row-copy">
          <strong>{teacher.name}</strong>
          <span>{teacher.email || "No email recorded"}</span>
          <span>Status: {teacher.readableStatus}</span>
        </span>
      </button>
    );
  };

  const renderRecommendedSummary = () => (
    <div className="resolve-login-summary">
      <h3>Recommended action</h3>
      <div className="resolve-login-summary-table">
        <span>Teacher</span>
        <span>{selectedSummary?.name || "Choose a teacher"}</span>
        <span>Account status</span>
        <span>{selectedSummary?.readableStatus || "-"}</span>
        <span>Next step</span>
        <span>{recommendation?.label || "Choose what help is needed"}</span>
      </div>
    </div>
  );

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="resolve-login-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherSearch.trim()) {
      return <p className="resolve-login-empty">Start typing a teacher name.</p>;
    }

    if (selectedTeacher) {
      return renderTeacherRow(selectedTeacher);
    }

    if (visibleTeachers.length < 1) {
      return <p className="resolve-login-empty">No matching teachers found.</p>;
    }

    return visibleTeachers.slice(0, 5).map(renderTeacherRow);
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Help a Teacher Log In</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="resolve-login-intro">Find the teacher who cannot log in.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group controlId="resolveLoginTeacher">
          <Form.Label>Which teacher cannot log in?</Form.Label>
          <Form.Text as="div" className="resolve-login-field-label">
            Search teacher
          </Form.Text>
          <Form.Control
            type="search"
            value={teacherSearch}
            onChange={handleTeacherSearchChange}
            aria-label="Search teacher"
          />
          <Form.Text className="text-muted">Start typing a teacher name.</Form.Text>
        </Form.Group>
        <div className="resolve-login-results" aria-label="Matching teachers">
          {renderTeacherResults()}
        </div>
        {selectedTeacher && (
          <Form.Group className="resolve-login-section" controlId="resolveLoginIssue">
            <Form.Label>What does {selectedTeacher.name} need help with?</Form.Label>
            <Form.Select
              value={loginIssue}
              onChange={(event) => {
                setLoginIssue(event.target.value);
                setError("");
              }}
              aria-label="Login issue"
            >
              <option value="">Choose the login problem</option>
              {LOGIN_ISSUES.map((issue) => (
                <option key={issue.value} value={issue.value}>{issue.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="resolve-login-footer-note">
          {!loginIssue
            ? "Choose the login problem."
            : selectedSummary?.footerCopy || "Choose a teacher."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handlePrimaryAction}
          disabled={!loginIssue || !selectedTeacher || isSubmitting}
        >
          Review recommended action
        </Button>
      </Modal.Footer>
    </>
  );

  const renderActions = () => {
    const setupTeacher = actionTeacher || selectedTeacher;

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Recommended Action for {setupTeacher.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="resolve-login-intro">Review the recommended action for {setupTeacher.name}.</p>
          {error && <Alert variant="danger">{error}</Alert>}
          {renderRecommendedSummary()}
        </Modal.Body>
        <Modal.Footer>
          <span className="resolve-login-footer-note">Recommended action ready.</span>
          <Button variant="outline-secondary" onClick={() => setStage("form")}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={runRecommendedAction}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : recommendation?.label}
          </Button>
        </Modal.Footer>
      </>
    );
  };

  const renderSuccess = () => {
    const teacher = sentTeacher || selectedSummary;
    const firstName = teacher?.name?.split(" ")[0] || "The teacher";
    const setupLinkSent = sentAction === "Send setup link";

    return (
      <>
        <Modal.Header closeButton>
          <Modal.Title as="h2">Login Help Sent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="resolve-login-intro">
            {setupLinkSent
              ? `${firstName} can use the link to finish setting up the account.`
              : `${firstName} can use the link to choose a new password.`}
          </p>
          <div className="resolve-login-success">
            <span className="resolve-login-ok">OK</span>
            <span>
              <strong>
                {setupLinkSent
                  ? `A setup link has been sent to ${teacher?.name || "the teacher"}.`
                  : `A password reset link has been sent to ${teacher?.name || "the teacher"}.`}
              </strong>
              <span>
                {setupLinkSent
                  ? `${firstName} can finish creating the account.`
                  : `${firstName} can use the link to choose a new password.`}
              </span>
            </span>
          </div>
          <div className="resolve-login-next">
            <strong>What next?</strong>
            <span>
              {embedded
                ? "Done returns to SENSAI. Resolve another login issue repeats the action."
                : "Done returns to Help Centre home. Resolve another login issue repeats the action."}
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="resolve-login-footer-note">Task complete.</span>
          <Button variant="outline-secondary" onClick={resetForAnotherIssue}>
            Resolve another login issue
          </Button>
          {onUpdateUserEmail && (
            <Button
              variant="outline-secondary"
              onClick={() => onUpdateUserEmail({
                teacherName: teacher?.name,
                currentEmail: teacher?.email
              })}
            >
              Update user email
            </Button>
          )}
          <Button variant="primary" onClick={handleDone}>
            Done
          </Button>
        </Modal.Footer>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-resolve-login-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "actions" && renderActions()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherResolveLoginIssueModal;
